import { FC, PropsWithChildren, useEffect, useState } from "react";
import classes from './studentsMove.module.scss'
import { useGlobalLoadingActions } from "../../../entities/globalLoading";
import { ITutor, tutorService } from "../../../entities/tutor";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { DropDownListSelected } from "../../../shared/ui/dropDownSelected";
import { Modal } from "../../../shared/ui/modal";
import { ConfirmationAction } from "../../../shared/ui/confirmationAction";
import { IItem } from "../../../shared/model/types";
import { studentService } from "../../../entities/student";
import { MyButton, MyButtonSecondary } from "../../../shared/ui/button";

interface IProps {
    title: string;
    tutorId: number;
    studentId?: number;
    onSelected?: (newTutorId: ITutor) => void;
    request?: () => Promise<{ tutors: ITutor[], tutors_count: number }>;
}

export const StudentsMove: FC<IProps & PropsWithChildren> = ({title, tutorId, studentId, onSelected, request}) => {

    const {setIsLoading: setIsLoadingGlobal} = useGlobalLoadingActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()
    const [open, setOpen] = useState<boolean>(false)
    const [confirmOpen, setConfirmOpen] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isMoving, setIsMoving] = useState<boolean>(false)
    const [error, setError] = useState<string>('')
    const [tutors, setTutors] = useState<ITutor[]>([])

    const [selectedTutor, setSelectedTutor] = useState<number>(0)

    const getTutorValue = (tutor: ITutor) => tutor.user_id || tutor.id

    const getTutors = async() => {
        try{
            setIsLoading(true)
            const tutorsRes = request ? await request() : await tutorService.getAll()
            setTutors(tutorsRes.tutors)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setError(e.message)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setError(e.message)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setError('Ошибка')
                setGlobalMessage({message: 'Ошибка', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }
    
    const onMove = async () => {
        if(!selectedTutor || tutorId === selectedTutor) return
        try{
            setIsMoving(true)
            setIsLoadingGlobal(true)
            setError('')
            await studentService.move(tutorId, selectedTutor, studentId)
            setGlobalMessage({message: 'Успешная смена исполнителя', type: 'ok'})
            if(onSelected){
                const target = tutors.find(t => getTutorValue(t) === selectedTutor)
                if(target){
                    onSelected(target)
                }
            }
            setOpen(false)
            setConfirmOpen(false)
            setSelectedTutor(0)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setError(e.message)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setError(e.message)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setError('Ошибка')
                setGlobalMessage({message: 'Ошибка', type: 'error'})
            }
        }
        finally{
            setIsMoving(false)
            setIsLoadingGlobal(false)
        }

    }

    const onClose = () => {
        if(isMoving) return
        setSelectedTutor(0)
        setOpen(false)
        setConfirmOpen(false)
        setError('')
    }

    const onSelectedTutor = (item: IItem) => {
        return (selected: boolean) => {
            const target = tutors.find(t => getTutorValue(t) === item.id)
            if(target && selected) {
                setSelectedTutor(getTutorValue(target))
                setError('')
            }
            else{
                setSelectedTutor(0)
            }
        }
    }

    const prepareConfirm = () => {
        if(!selectedTutor){
            setError('Выберите исполнителя')
            return
        }

        setError('')
        setConfirmOpen(true)
    }
    
    useEffect(() => {
        if(open && tutors.length === 0){
            getTutors()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const tutorItems = tutors
        .filter(t => getTutorValue(t) !== tutorId)
        .map(t => ({name: t.full_name, id: getTutorValue(t)}))

    const selectedTutorName = tutorItems.find(t => t.id === selectedTutor)?.name || ''

    return (
        <section className={classes.container}>
            <section className={classes.buttonOpen}>
                <MyButton 
                    onClick={() => setOpen(true)}
                >
                    <section className={classes.buttonText}> 
                        {title}
                    </section>
                </MyButton>
            </section>
            <Modal setOpen={(value) => !value && onClose()} open={open}>
                {confirmOpen ? (
                    <ConfirmationAction
                        onClick={onMove}
                        setOpen={(value) => {
                            if(!value) setConfirmOpen(false)
                        }}
                        title={`Перенести студентов к новому исполнителю${selectedTutorName ? ` ${selectedTutorName}` : ''}?`}
                        type="send"
                        isLoading={isMoving}
                    />
                ) : (
                    <section className={classes.modal}>
                        <section className={classes.title}>{title}</section>
                        <section className={classes.choose}>
                            <DropDownListSelected 
                                isLoading={isLoading}
                                selectedIdItems={selectedTutor ? [selectedTutor] : []}
                                items={tutorItems}
                                onSelected={onSelectedTutor}
                                plainSelected
                                search
                                searchPlaceholder="Поиск по исполнителям"
                                placeholder="Выберите исполнителя"
                                emptyText="Исполнители не найдены"
                            />
                        </section>
                        {!isLoading && tutorItems.length === 0 && (
                            <section className={classes.muted}>Нет доступных исполнителей для переноса</section>
                        )}
                        {error && <section className={classes.error}>*{error}</section>}
                        <section className={classes.actions}>
                            <MyButtonSecondary onClick={onClose}>Отмена</MyButtonSecondary>
                            <MyButton
                                onClick={prepareConfirm}
                                disabled={isLoading || tutorItems.length === 0}
                            >
                                Продолжить
                            </MyButton>
                        </section>
                    </section>
                )}
            </Modal>
        </section>
    )
}
